/**
 * Number format helper used by all 4 export format generators
 * (CSV, XML, JSON, Fixed-width). Modal-redesign WU-D (2026-05-10).
 *
 * Mirrors the formatDateValue (WU-6) precedence pattern: per-field
 * `numberFormat` (FieldContent.numberFormat) overrides format-level
 * defaults; both get consumed via `resolveColumnValue` so generators
 * never branch on dataType themselves.
 *
 * Browser-safe: no `node:*` imports. Lives in `@capture/shared` so the
 * worker (server) and web (browser) consume the same logic.
 *
 * Format shape (matches `FieldContent['numberFormat']`):
 *   - decimals?: number               — fixed decimal places (zero-pads)
 *   - thousandsSeparator?: boolean    — render `1,234,567` vs `1234567`
 *   - currencySymbol?: string         — prefix ($, €, USD)
 *   - negativeStyle?: 'minus' | 'parens'  — `-1234.5` vs `(1234.5)`
 *
 * Pass-through policy:
 *   - null / undefined / empty string / NaN / Infinity → ''
 *   - non-numeric strings → ''
 *   - numeric strings parse and format normally
 *   - empty format object → number's plain string representation
 */
function applyThousandsSeparator(intStr) {
    // The integer portion is grouped left-to-right in 3-digit chunks.
    // Regex on a digit-only string is safe — no leading sign or decimals
    // by the time we reach this helper.
    return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}
export function formatNumberValue(value, format) {
    if (value === null || value === undefined || value === "") {
        return "";
    }
    // Coerce to number; reject non-finite results.
    const num = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(num)) {
        return "";
    }
    const isNegative = num < 0;
    const absNum = Math.abs(num);
    // 1. Fix decimals (or preserve input precision when not specified).
    const fixed = format.decimals != null && format.decimals >= 0
        ? absNum.toFixed(format.decimals)
        : String(absNum);
    // 2. Optional thousands separator on the integer portion only.
    let body = fixed;
    if (format.thousandsSeparator) {
        const dotIdx = fixed.indexOf(".");
        const intPart = dotIdx >= 0 ? fixed.slice(0, dotIdx) : fixed;
        const fracPart = dotIdx >= 0 ? fixed.slice(dotIdx) : "";
        body = applyThousandsSeparator(intPart) + fracPart;
    }
    // 3. Prefix currency symbol (after thousands grouping, before
    //    negative wrap).
    if (format.currencySymbol) {
        body = format.currencySymbol + body;
    }
    // 4. Apply negative style. Zero is never wrapped — `-0` would be
    //    confusing, and `Math.abs` already collapsed it.
    if (isNegative && absNum > 0) {
        if (format.negativeStyle === "parens") {
            return `(${body})`;
        }
        return `-${body}`;
    }
    return body;
}
//# sourceMappingURL=number-formatting.js.map